import { useCallback, useMemo } from 'react';
import { FilterPipelineFilter } from 'record-it-components/hooks/useFilterPipeline/useFilterPipeline';
import { EntityName } from 'record-it-time-common/EntityName';
import { ArrayUtils } from 'record-it-utils/utils/ArrayUtils/ArrayUtils';
import { AppEntityManager } from '../../../classes/entities/AppEntityManager';
import { User } from '../../../classes/entities/User/userTypes';
import { WorkTimeEntry } from '../../../classes/entities/WorkTimeEntry/workTimeEntryTypes';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { CurrentUserContextValue } from '../../../context/CurrentUserContext';
import { createUserGroupIdUrlFilter } from '../../../filters/userGroupIdUrlFilter/userGroupIdUrlFilter';
import { createUserIdUrlFilter } from '../../../filters/userIdUrlFilter/userIdUrlFilter';
import {
  Callback,
  useComputedEntityResult
} from '../../../hooks/entity/useComputedEntityResult/useComputedEntityResult';
import { useEntityManager } from '../../../hooks/entity/useEntityManager';

export function useWorkTimeAdminPageFilters({
  userGroupId,
  userId,
  getRequiredCurrentUserPermissions
}: {
  userGroupId: string;
  userId: string;
  getRequiredCurrentUserPermissions: CurrentUserContextValue['getRequiredCurrentUserPermissions'];
}): Array<FilterPipelineFilter<WorkTimeEntry, any>> {
  const { entityManager } = useEntityManager();
  const users = useUsersOfUserGroup(userGroupId);

  return useMemo(() => {
    const permissions = getRequiredCurrentUserPermissions();

    return [
      createUserGroupIdUrlFilter({
        userGroups:
          permissions.getEditableUserGroupsForTimeTrackingActivityCompilations(),
        currentUserGroupId: userGroupId,
        createUrl: (urlUserGroupId) => {
          return RoutePaths.adminWorkTime(
            urlUserGroupId,
            getFirstUserIdOfUserGroup({
              userGroupId: urlUserGroupId,
              entityManager
            })
          );
        }
      }),

      createUserIdUrlFilter({
        users,
        currentUserId: userId,
        createUrl: (urlUserId) =>
          RoutePaths.adminWorkTime(userGroupId, urlUserId)
      })
    ];
  }, [
    getRequiredCurrentUserPermissions,
    entityManager,
    users,
    userGroupId,
    userId
  ]);
}

function getFirstUserIdOfUserGroup({
  userGroupId,
  entityManager
}: {
  userGroupId: string;
  entityManager: AppEntityManager;
}): string {
  const relations = entityManager.entityRepositoryContainer
    .getByEntityName(EntityName.UserGroupToUser)
    .getByUserGroupId(userGroupId);
  const firstRelation = relations[0];

  if (!firstRelation) {
    throw new Error(`no relations found for userGroup ${userGroupId}`);
  }

  return firstRelation.userId;
}

function useUsersOfUserGroup(userGroupId: string): Array<User> {
  const callback = useCallback<
    Callback<[EntityName.User, EntityName.UserGroupToUser], Array<User>>
  >(
    ({ userGroupToUserRepository, userRepository }) => {
      return userGroupToUserRepository
        .getByUserGroupId(userGroupId)
        .map((relation) => {
          return userRepository.getById(relation.userId);
        })
        .filter((user): user is User => !!user);
    },
    [userGroupId]
  );

  return useComputedEntityResult({
    entityNames: [EntityName.User, EntityName.UserGroupToUser],
    callback,
    resultsAreEqual: (a, b) => ArrayUtils.areShallowEqual(a, b)
  });
}
